import { useState, FC } from 'react';
import { Box, IconButton, Typography, Paper, Tooltip, Fade, Collapse } from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import CheckIcon from '@mui/icons-material/Check';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import WebexLogo from '@/shared/components/Icons/BrandIcons/WebexLogo';
import useWebexStore from '../store/webex.store';

interface MeetingLinkProps {
  isGuest?: boolean;
}

// Popup kept at module scope so guest logout / tab close can kill the Webex
// meeting window — otherwise the guest's media session outlives the Nexgen session.
let guestMeetingWindow: Window | null = null;

export const closeGuestWebexMeetingWindow = () => {
  guestMeetingWindow?.close();
  guestMeetingWindow = null;
};

/** Maps the backend's failure code to guest-facing copy. Mirrors WEBEX_GUEST_JOIN_ERROR_CODES. */
const guestFallbackMessage = (reason: string | null): string => {
  switch (reason) {
    case 'WEBEX_GUEST_TOKEN_MINT_FAILED':
      return 'Joining here is temporarily unavailable — retrying';
    case '404':
      return "The meeting hasn't started yet — join in Webex";
    default:
      return 'Join in the Webex app';
  }
};

const MeetingLink: FC<MeetingLinkProps> = ({ isGuest = false }) => {
  const guestJoinUnavailable = useWebexStore((s) => s.guestJoinUnavailable);
  const guestJoinUnavailableReason = useWebexStore((s) => s.guestJoinUnavailableReason);
  // Auto-expand only when this panel is the guest's actual way into the meeting.
  const [isExpanded, setIsExpanded] = useState(isGuest && guestJoinUnavailable);
  const [copied, setCopied] = useState(false);

  const link = useWebexStore((s) => s.personalMeeting?.webLink);

  const copyLink = () => {
    if (link) {
      navigator.clipboard.writeText(link).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1000);
      });
    }
  };

  const openLink = () => {
    if (link) {
      const width = 1200;
      const height = 900;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;

      const newWindow = window.open(
        link,
        'Webex Meeting',
        `width=${width},height=${height},top=${top},left=${left}`
      );

      if (newWindow) {
        guestMeetingWindow = newWindow;
        newWindow.focus();
      }
    }
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        left: 16,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        zIndex: 1000,
        ...(isGuest ? { top: 80 } : { bottom: 80 }), // Adjust position for guests
      }}
    >
      {/* Webex Logo Button */}
      <Tooltip title={'Webex Meeting'} arrow placement='top'>
        <IconButton
          onClick={toggleExpanded}
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            width: 48,
            height: 48,
            boxShadow: 3,
            '&:hover': {
              bgcolor: 'primary.dark',
              boxShadow: 6,
            },
            transition: 'all 0.3s ease',
          }}
        >
          <WebexLogo fontSize='medium' />
        </IconButton>
      </Tooltip>

      {/* Collapsible Meeting Controls */}
      <Collapse in={isExpanded} orientation='horizontal' timeout={300}>
        <Fade in={isExpanded} timeout={400}>
          <Paper
            elevation={4}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              p: 1.5,
              bgcolor: 'background.paper',
              borderRadius: 2,
              minWidth: 180,
            }}
          >
            <Typography
              variant='body2'
              fontWeight={600}
              color='text.primary'
              sx={{ mb: 0.5, textAlign: 'center' }}
            >
              Webex Meeting
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'center' }}>
              {/* Copy Link Button */}
              <Tooltip
                title={copied ? 'Meeting Link Copied correctly!' : 'Copy Meeting URL'}
                placement='bottom'
                arrow
              >
                <IconButton
                  onClick={copyLink}
                  size='small'
                  sx={{
                    bgcolor: copied ? 'success.main' : 'primary.main',
                    color: 'white',
                    '&:hover': {
                      bgcolor: copied ? 'success.dark' : 'primary.dark',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  {copied ? <CheckIcon fontSize='small' /> : <LinkIcon fontSize='small' />}
                </IconButton>
              </Tooltip>

              {/* Guests only, and only as the FALLBACK — when in-app join works there is no
                  use for an external popup. */}
              {isGuest && guestJoinUnavailable && (
                <Tooltip title='Open Meeting' arrow>
                  <IconButton
                    onClick={openLink}
                    size='small'
                    sx={{
                      bgcolor: 'secondary.main',
                      color: 'white',
                      '&:hover': {
                        bgcolor: 'secondary.dark',
                      },
                    }}
                  >
                    <OpenInNewIcon fontSize='small' />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            {/* Why the in-app join isn't available — the reason was recorded but never shown. */}
            {isGuest && guestJoinUnavailable && (
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ mt: 0.5, textAlign: 'center', maxWidth: 190, lineHeight: 1.3 }}
              >
                {guestFallbackMessage(guestJoinUnavailableReason)}
              </Typography>
            )}
          </Paper>
        </Fade>
      </Collapse>
    </Box>
  );
};

export default MeetingLink;
